import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminUsersQuery } from '../../../features/admin/hooks/use-admin-api'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsersPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type UserItem = {
  id: string
  name: string | null
  email: string
  role: string
  userType: string | null
  onboardingCompleted: boolean
  lastAccessedAt: string | null
  createdAt: string
  googleLinked: boolean
}

const columns: TableColumn<UserItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (user) => (
      <Link
        to="/admin/users/$id"
        params={{ id: user.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {user.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Name',
    render: (user) => <CellPrimary>{user.name || '\u2014'}</CellPrimary>,
  },
  {
    header: 'Email',
    render: (user) => <CellText>{user.email}</CellText>,
  },
  {
    header: 'Status',
    width: '300px',
    render: (user) => (
      <div className="flex flex-wrap gap-1 uppercase">
        <Badge variant={user.role === 'ADMIN' ? 'destructive' : 'secondary'} size="sm">
          {user.role}
        </Badge>
        {user.userType && (
          <Badge variant={user.userType === 'FOUNDER' ? 'primary' : 'outline-primary'} size="sm">
            {user.userType}
          </Badge>
        )}
        <Badge variant={user.onboardingCompleted ? 'success' : 'secondary'} size="sm">
          {user.onboardingCompleted ? 'Onboarded' : 'Pending'}
        </Badge>
        {/* Email code always works regardless — this only flags a linked
            Google account, so it's additive, never shown as an either/or
            with an "Email" badge. */}
        {user.googleLinked && (
          <Badge variant="outline" size="sm">
            Google
          </Badge>
        )}
      </div>
    ),
  },
  {
    header: 'Last Active',
    width: '200px',
    render: (user) => (
      <CellText>{user.lastAccessedAt ? formatDate(user.lastAccessedAt) : '\u2014'}</CellText>
    ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (user) => <CellText>{formatDate(user.createdAt)}</CellText>,
  },
]

function AdminUsersPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const usersQuery = useAdminUsersQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
  })

  const users = usersQuery.data?.data?.items ?? []
  const total = usersQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <SearchInput
        placeholder="Search users..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={users}
        isLoading={usersQuery.isLoading}
        emptyMessage="No users found."
        keyExtractor={(u) => u.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
